import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { getConversation } from '../../api/aurora';
import { AppIcon } from '../../components/AppIcon';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}
interface ConversationData {
  id: string;
  title: string;
  updatedAt: string;
  messages: Message[];
}
export function AuroraConversationView() {
  const {
    conversationId
  } = useParams();
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const [conversation, setConversation] = useState<ConversationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!token || !conversationId) return;
    const fetchConv = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const data = await getConversation(token, conversationId);
        setConversation(data);
      } catch (err) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchConv();
  }, [token, conversationId]);
  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const renderMarkdown = (content: string) => {
    const lines = content.split('\n');
    const processed = lines.map(line => {
      if (line.startsWith('### ')) return `<h4 class="text-[15px] font-semibold text-text-primary mt-3 mb-1">${line.slice(4)}</h4>`;
      if (line.startsWith('## ')) return `<h3 class="text-[16px] font-semibold text-text-primary mt-4 mb-1">${line.slice(3)}</h3>`;
      if (line.startsWith('# ')) return `<h2 class="text-[17px] font-bold text-text-primary mt-4 mb-2">${line.slice(2)}</h2>`;
      if (line.match(/^- /)) return `<div class="flex gap-2 ml-1"><span>•</span><span>${line.slice(2)}</span></div>`;
      if (line.trim() === '') return '<div class="h-2"></div>';
      return `<p>${line}</p>`;
    });
    const html = processed.join('').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return <div className="space-y-0.5" dangerouslySetInnerHTML={{
      __html: html
    }} data-id="element-649" />;
  };
  if (isLoading) {
    return <div className="flex flex-col h-[calc(100vh-64px)] md:h-[calc(100vh-80px)] bg-background" data-id="element-650">
        <div className="bg-white border-b border-border px-4 py-3 flex items-center shadow-sm" data-id="element-651">
          <SkeletonBlock width="40px" height="40px" borderRadius="50%" className="mr-3" data-id="element-652" />
          <SkeletonBlock width="150px" height="24px" data-id="element-653" />
        </div>
        <div className="flex-1 p-4 space-y-6" data-id="element-654">
          <SkeletonBlock height="80px" borderRadius="20px" className="ml-auto w-2/3" data-id="element-655" />
          <SkeletonBlock height="120px" borderRadius="20px" className="mr-auto w-2/3" data-id="element-656" />
        </div>
      </div>;
  }
  if (error || !conversation) {
    return <div className="flex flex-col h-[calc(100vh-64px)] md:h-[calc(100vh-80px)] bg-background" data-id="element-657">
        <div className="bg-white border-b border-border px-4 py-3 flex items-center shadow-sm" data-id="element-658">
          <button onClick={() => navigate('/app/aurora/history')} className="mr-3" data-id="element-659">
            <ArrowLeft size={24} className="text-text-primary" data-id="element-660" />
          </button>
          <h1 className="text-[18px] font-semibold text-text-primary" data-id="element-661">Error</h1>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-662">
          <MessageSquare size={48} className="text-danger mb-4" data-id="element-663" />
          <h2 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-664">
            Conversation not found
          </h2>
          <Button onClick={() => navigate('/app/aurora/history')} className="mt-4" data-id="element-665">
            Back to History
          </Button>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-[calc(100vh-64px)] md:h-[calc(100vh-80px)] bg-background" data-id="element-666">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-border px-4 py-3 flex items-center z-10 shadow-sm" data-id="element-667">
        <button onClick={() => navigate('/app/aurora/history')} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors mr-2" aria-label="Go back" data-id="element-668">
          <ArrowLeft size={24} className="text-text-primary" data-id="element-669" />
        </button>
        <div className="flex-1 min-w-0" data-id="element-670">
          <h1 className="text-[16px] font-semibold text-text-primary truncate" data-id="element-671">
            {conversation.title || 'Conversation'}
          </h1>
          <p className="text-[12px] text-text-secondary" data-id="element-672">
            {new Date(conversation.updatedAt).toLocaleDateString([], {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar" data-id="element-673">
        <div className="space-y-6 pb-4" data-id="element-674">
          {conversation.messages.map(msg => <motion.div key={msg.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-675">
              {msg.role === 'assistant' && <div className="flex-shrink-0 mr-2 mt-1" data-id="element-676">
                  <AppIcon size={28} data-id="element-677" />
                </div>}
              <div className={`max-w-[80%] md:max-w-[70%] flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`} data-id="element-678">
                <div className={`px-4 py-3 rounded-[20px] text-[15px] leading-relaxed ${msg.role === 'user' ? 'bg-primary text-white rounded-tr-[4px]' : 'bg-white text-text-primary shadow-sm border border-border/50 rounded-tl-[4px]'}`} data-id="element-679">
                  {msg.role === 'assistant' ? renderMarkdown(msg.content) : <span className="whitespace-pre-wrap" data-id="element-680">{msg.content}</span>}
                </div>
                <span className="text-[11px] text-text-secondary mt-1 px-1" data-id="element-681">
                  {formatTime(msg.createdAt)}
                </span>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Footer Action */}
      <div className="flex-shrink-0 bg-white border-t border-border p-4 pb-[calc(16px+env(safe-area-inset-bottom))]" data-id="element-682">
        <Button onClick={() => navigate('/app/aurora', {
        state: {
          conversationId,
          messages: conversation?.messages || []
        }
      })} className="w-full max-w-[400px] mx-auto" data-id="element-683">
          Continue this conversation
        </Button>
      </div>
    </div>;
}